import React, { Component } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBolt,
  faInbox,
  faFolderOpen,
  faEuroSign,
  faStamp,
} from "@fortawesome/free-solid-svg-icons";
import moment from "moment";

import LiveKpiCard from "./LiveKpiCard";
import LiveFeed from "./LiveFeed";
import LiveBreakdown from "./LiveBreakdown";

import { getCurrencyValue } from "../../../utils/general";

import "../../../style/liveTracking.css";

const FALLBACK_REFRESH_MS = 60 * 1000;

export default class LiveTracking extends Component {
  constructor(props) {
    super(props);
    this.state = { countdown: Math.round(this.refreshMs() / 1000) };
  }

  refreshMs = () => this.props.refreshMs || FALLBACK_REFRESH_MS;

  // The countdown is display only. The parent view owns the single fetch
  // interval; driving a second one from here made the page fetch twice per
  // cycle at drifting offsets (and fired a side effect from inside a setState
  // updater, which React is free to call more than once).
  componentDidMount() {
    this.tick = setInterval(() => {
      this.setState((s) => ({ countdown: s.countdown > 0 ? s.countdown - 1 : 0 }));
    }, 1000);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.lastFetchAt !== this.props.lastFetchAt ||
      prevProps.refreshMs !== this.props.refreshMs
    ) {
      this.setState({ countdown: Math.round(this.refreshMs() / 1000) });
    }
  }

  componentWillUnmount() {
    clearInterval(this.tick);
  }

  formatCurrency = (v) => getCurrencyValue(v || 0);

  // Past a minute a raw "118s" is hard to read at a glance.
  formatCountdown = (s) =>
    s >= 60 ? `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s` : `${s}s`;

  // A frozen "Sync" clock is normal when nothing changed, and also exactly what
  // a broken ETL looks like. Compare the last RUN against the cadence so the
  // two stop rendering identically.
  getSyncHealth(source) {
    if (!source || source.type !== "duckdb") return null;
    if (source.lastRunOk === false) return "error";
    if (!source.lastRunAt) return "unknown";
    const interval = (source.intervalMinutes || 5) * 60 * 1000;
    const age = moment().diff(moment(source.lastRunAt, "YYYY-MM-DD HH:mm:ss"));
    return age > interval * 2 ? "stale" : "ok";
  }

  render() {
    const { data, isLoading } = this.props;
    const today = data?.today || {};
    const trend = data?.trend || {};
    const refLabel = "J-7 (journée entière)";
    const source = data?.dataSource;
    const health = this.getSyncHealth(source);

    return (
      <div className="live-tracking">
        <div className="lt-header">
          <div className="lt-title">
            <FontAwesomeIcon icon={faBolt} />
            Activité du jour
          </div>
          <div className="lt-meta">
            <span className="lt-pulse">
              <span className="lt-pulse-dot" /> LIVE
            </span>
            <span title="Prochain rafraîchissement — calé sur la cadence de la synchro">
              Refresh
              <span className="lt-meta-value">
                {this.formatCountdown(this.state.countdown)}
              </span>
            </span>
            <span title="Horodatage de la donnée la plus récente détenue">
              Données
              <span className="lt-meta-value">
                {source?.lastSyncAt
                  ? moment(source.lastSyncAt).format("HH:mm:ss")
                  : "—"}
              </span>
            </span>
            {health ? (
              <span
                title={
                  health === "error"
                    ? `Dernière synchro en échec : ${source.lastRunError || "erreur inconnue"}`
                    : health === "stale"
                    ? "La synchro ne tourne plus dans les délais attendus"
                    : `Synchro toutes les ${source.intervalMinutes || 5} min`
                }
              >
                Synchro
                <span className={`lt-meta-value lt-meta-value--${health}`}>
                  {source.lastRunAt
                    ? moment(source.lastRunAt).format("HH:mm:ss")
                    : "—"}
                  {health === "error" ? " ⚠" : health === "stale" ? " ⚠" : ""}
                </span>
              </span>
            ) : null}
            <span>
              Source
              <span className="lt-meta-value">
                {source?.type === "duckdb"
                  ? `DuckDB · ${source.intervalMinutes || 5} min`
                  : "MySQL"}
              </span>
            </span>
          </div>
        </div>

        {isLoading ? <div className="lt-loading-bar" /> : null}

        <div className="lt-kpi-grid">
          <LiveKpiCard
            label="Missions reçues"
            icon={faInbox}
            value={today.missionCount || 0}
            trend={trend.mission}
            refLabel={refLabel}
            variant="cyan"
          />
          <LiveKpiCard
            label="Dossiers déposés"
            icon={faFolderOpen}
            value={today.closingCount || 0}
            trend={trend.closing}
            refLabel={refLabel}
            variant="emerald"
          />
          <LiveKpiCard
            label="SD29 envoyés"
            icon={faStamp}
            value={today.sd29Count || 0}
            trend={trend.sd29}
            refLabel={refLabel}
            variant="amber"
          />
          <LiveKpiCard
            label="Facturation HT"
            icon={faEuroSign}
            value={today.ca || 0}
            trend={trend.ca}
            refLabel={refLabel}
            variant="magenta"
            format={this.formatCurrency}
          />
        </div>

        <div className="lt-grid-main">
          <LiveFeed events={data?.feed || []} />
          <LiveBreakdown breakdown={data?.breakdown} />
        </div>
      </div>
    );
  }
}
